/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild
} from "@angular/core";
import { concatMap, debounceTime, distinctUntilChanged, filter, finalize, from, map, skip, Subject, Subscription, switchMap, take, takeUntil } from "rxjs";
import { BehaviorSubject, of } from "rxjs";
import { SelectionModel } from "@angular/cdk/collections";
import { MatTableDataSource } from "@angular/material/table";
import { TranslateService } from "@ngx-translate/core";
import PQueue from "p-queue";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { select, Store } from "@ngrx/store";
import { DocumentPreviewComponent, VNCInputComponent, VncLibraryService } from "@uxf/vnc-library";
import { filesize } from "filesize";
import { FileInfo, OwnCloudService } from "../../owncloud.service";
import { RootState, getProps } from "src/app/reducers";
import { PreferenceState } from "src/app/preference/store/reducers";
import { MailConstants } from "src/app/common/utils/mail-constants";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import { CommonDialogComponent } from "../common-dialog/common-dialog.component";
import { FormControl } from "@angular/forms";
import { OwncloudDetailDialogComponent } from "../owncloud-detail/owncloud-detail.component";
import { FilePreviewDialogComponent } from "src/app/mail/shared/components/file-preview-dialog/file-preview-dialog.component";
import { MailService } from "src/app/mail/shared/services/mail-service";
import { ConfigService } from "src/app/config.service";
import { environment } from "src/environments/environment";
import { ElectronService } from "src/app/services/electron.service";
import { SaveSendMessage } from "src/app/mail/shared/models";
import { ConversationRepository } from "src/app/mail/repositories/conversation.repository";
import { Router } from "@angular/router";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { CommonUtils } from "src/app/common/utils/common-util";
import moment from "moment";
import distanceInWordsToNow from "date-fns/distance_in_words_to_now";
import enUS from "date-fns/locale/en";
import de from "date-fns/locale/de";
import fr from "date-fns/locale/fr";
import ru from "date-fns/locale/ru";
import { ConfirmArgs } from "src/app/mail/models/confirm-args";
import { MailOperations } from "src/app/common/enums/mail-enum";
import { ConfirmDialogComponent } from "src/app/mail/confirm-dialog/confirm-dialog.component";



@Component({
  selector: "vp-owncloud",
  templateUrl: "./owncloud.html",
  styleUrls: ["./owncloud.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class OwnCloudComponent implements OnInit, OnDestroy {
  @Input() searchControl: FormControl = new FormControl("", []);
  @Input() selection = new SelectionModel<FileInfo>(true, []);
  @Input() currentFolder: any;
  @Input() selectedTarget: any;
  @Output() search = new EventEmitter();
  @Output() filterChange = new EventEmitter();
  @Output() selectionChanged = new EventEmitter();
  @ViewChild("renameInput") renameInput: VNCInputComponent;
  files$ = new BehaviorSubject<FileInfo[]>([]);
  displayedColumns: string[] = ["select", "name", "action", "contentLength", "lastModified"];
  sharedColumns: string[] = ["name", "action", "lastModified"];
  dataSource = new MatTableDataSource<FileInfo>([]);
  lang = localStorage.getItem("portalLanguage") || "en";
  locale: any;
  triggerGallary = new Subject<any>();
  totalFiles = 0;
  totalFolders = 0;
  showMenu = false;
  isNextCloud: boolean = false;
  selectedFile: any;
  isLoading: boolean;
  selectionText: string;
  uploadQueue = new PQueue({ concurrency: 1 });
  directoryQueue = new PQueue({ concurrency: 1 });
  uploadFileUniqueId = 0;
  existingResources = [];
  success$ = new Subject<any>();
  uploading = new BehaviorSubject<boolean>(false);
  progress$ = new BehaviorSubject<any>({});
  errors$ = new Subject<any>();
  itemNameControl: FormControl = new FormControl("", []);
  fileToUpdate: any;
  isPending: boolean;
  isLoggedIn: boolean;
  userJID: any;
  uploadedList = [];
  teamFolders = [];
  currentFilter = new BehaviorSubject<string>("all");
  newFolderObject: any;
  newFolderId: any;
  checkedLastTime: boolean;
  tags: any[] = [];
  private isAlive$ = new Subject<boolean>();
  isDialog: any;
  isMobileScreen: boolean = false;
  isMobileSearch: boolean = false;
  selectedFolder = null;
  officeContent: string = "COLLAB";
  isCancelUpload: boolean = false;
  fileUploadSubscriptions: Subscription[] = [];
  uploadFail: boolean = false;

  constructor(
    private translate: TranslateService,
    private snackbar: MatSnackBar,
    private electronService: ElectronService,
    private dialog: MatDialog,
    private store: Store<RootState | PreferenceState>,
    private configService: ConfigService,
    private mailService: MailService,
    private ownCloudService: OwnCloudService,
    @Optional() private matDialogRef: MatDialogRef<OwnCloudComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private convRepository: ConversationRepository,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private vncLibraryService: VncLibraryService,
    private changeDetectorRef: ChangeDetectorRef) {
      this.isDialog = data?.isDialog;
      this.currentFilter.asObservable().pipe(takeUntil(this.isAlive$)).subscribe(v => {
        this.filterChange.emit(v);
      });

    this.store.pipe(select(getProps), distinctUntilChanged()).subscribe(props => {
      if (!!props) {
        const officePreview = props.find(item => item.name === "officePreview");
        if (this.configService.get("onlyOfficeApiUrl") && this.configService.get("collaboraBaseUrl") && this.configService.get("onlyOfficeApiUrl") !== "none" && this.configService.get("collaboraBaseUrl") !== "none") {
          this.officeContent = (!!officePreview && !!officePreview._content) ? officePreview._content : "OFC";
        } else {
          if (this.configService.get("onlyOfficeApiUrl") && this.configService.get("onlyOfficeApiUrl") !== "none") {
            this.officeContent = "OFC";
          }
          if (this.configService.get("collaboraBaseUrl") && this.configService.get("collaboraBaseUrl") !== "none") {
            this.officeContent = "COLLAB";
          }
        }
      } else {
        if (this.configService.get("onlyOfficeApiUrl") && this.configService.get("onlyOfficeApiUrl") !== "none") {
          this.officeContent = "OFC";
        }
        if (this.configService.get("collaboraBaseUrl") && this.configService.get("collaboraBaseUrl") !== "none") {
          this.officeContent = "COLLAB";
        }
      }
    });


    this.selection.changed.asObservable().pipe(takeUntil(this.isAlive$)).subscribe(v => {
      this.selectionChanged.emit(v);
      const text = [];
      const totalFiles = v.source.selected.filter(v => v.type === "file").length;
      const totalFolders = v.source.selected.filter(v => v.type === "dir").length;
      this.selectedFolder = totalFolders;
      if (totalFolders > 0) {
        let key = "TOTAL_FOLDER";
        if (totalFolders > 1) {
          key = "TOTAL_N_FOLDERS";
        }
        this.translate.get(key, { total: totalFolders }).pipe(take(1)).subscribe(text1 => {
          text.push(text1);
        });
      }
      if (totalFiles > 0) {
        let key = "TOTAL_FILE";
        if (totalFiles > 1) {
          key = "TOTAL_N_FILES";
        }
        this.translate.get(key, { total: totalFiles }).pipe(take(1)).subscribe(text1 => {
          text.push(text1);
        });
      }
      this.selectionText = text.join(", ");
      this.changeDetectorRef.markForCheck();
    });

     this.breakpointObserver
      .observe(["(max-width: 767px)"])
      .pipe(takeUntil(this.isAlive$))
      .subscribe((state: BreakpointState) => {
        this.isMobileScreen = state.matches;
        this.changeDetectorRef.markForCheck();
      });
  }

  updateTotal(v) {
    this.totalFolders = v.filter(v => v.name && v.type === "dir").length;
    this.totalFiles = v.filter(v => v.name && v.type !== "dir").length;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => {
      this.changeDetectorRef.markForCheck();
    }, 300);
  }

  checkIfElementExists(element) {
    const name = element.name || element;
    return this.dataSource.data.find(file => file.name === name);
  }

  getSharedOwner(element, ignoreShareType = false) {
    const currentUserDisplayName = (!!this.ownCloudService && !!this.ownCloudService.currentUser && !!this.ownCloudService.currentUser.displayname)
      ? this.ownCloudService.currentUser.displayname : "";
    const currentUserDisplayName2 = (!!this.ownCloudService && !!this.ownCloudService.currentUser && !!this.ownCloudService.currentUser["display-name"])
      ? this.ownCloudService.currentUser["display-name"] : "";

    const userDisplayName = !!currentUserDisplayName ? currentUserDisplayName : currentUserDisplayName2;
    if (!userDisplayName || !element?.ownerDisplayName) {
      return false;
    }

    const isSameUser = element.ownerDisplayName === userDisplayName;

    return (
      !isSameUser &&
      (element?.shareTypes === "" || ignoreShareType)
    );
  }

  ngOnInit(): void {
    this.ownCloudService.sharedReload$.subscribe((file) => {
      this.updateSharedFile(file);
    });
    this.ownCloudService.loginOC().subscribe();
    this.ownCloudService.getAllTags().pipe(takeUntil(this.isAlive$)).subscribe(tags => {
      this.tags = tags;
    });
    this.isNextCloud = this.configService.get("isNextCloud");
    this.ownCloudService.removeSharedFlag$.subscribe((id) => {
      const findIndex = this.dataSource.data.findIndex(v => v.fileId === id);
      if (findIndex !== -1) {
        const file = { ...this.dataSource.data[findIndex] };
        file.shareTypes = "";
        const updatedData = [...this.dataSource.data];
        updatedData[findIndex] = file;
        this.dataSource.data = this.sortFileList(updatedData);
        if (this.files$.value.find(v => v.fileId === file.fileId)) {
          const newFiles = [...this.files$.value];
          const fileIndex = newFiles.findIndex(v => v.fileId === file.fileId);
          if (fileIndex !== -1) {
            newFiles.splice(fileIndex, 1, file);
            this.files$.next(newFiles);
          }
        }
      }
      this.changeDetectorRef.markForCheck();
    });
    this.searchControl.valueChanges.pipe(skip(1), debounceTime(200), distinctUntilChanged(), takeUntil(this.isAlive$)
    , switchMap(keyword => {
      this.search.emit(keyword);
      this.resetData();
      if (keyword.length > 0) {
        this.currentFolder = null;
        this.isLoading = true;
        this.currentFilter.next("search");
        this.changeDetectorRef.markForCheck();
        return this.ownCloudService.search(keyword, 100);
      } else {
        if (!this.currentFolder) {
          this.home();
        }
        return of([]);
      }
    })).subscribe(v => {
      const teamFoldersAdd = this.teamFolders.filter(f => f.name.toLowerCase().indexOf(this.searchControl.value.toLowerCase()) > -1);
      const sumItems = (teamFoldersAdd.length > 0) ? [...v, ...teamFoldersAdd] : v;
      this.dataSource.data = this.sortFileList(sumItems.filter(v => v.name));
      this.isLoading = false;
      this.updateTotal(sumItems);
    });

    // this.store.select(getUserJID).pipe(filter(v => !!v)).pipe(take(1)).subscribe(jid => {
    //   this.userJID = jid;
    // });

    this.ownCloudService.isLoggedIn().pipe(filter(v => !!v), take(1))
      .subscribe(v => {
        this.ownCloudService.getTags();
        this.isNextCloud = this.configService.get("isNextCloud");
        console.log("occomponent isNextCloud: ", this.isNextCloud);
        this.isLoggedIn = v;
        this.store.pipe(select(getProps), take(1)).subscribe(props => {
          if (!!props) {
            const officePreview = props.find(item => item.name === "officePreview");
            if (this.configService.get("onlyOfficeApiUrl") && this.configService.get("collaboraBaseUrl") && this.configService.get("onlyOfficeApiUrl") !== "none" && this.configService.get("collaboraBaseUrl") !== "none") {
              this.officeContent = (!!officePreview && !!officePreview._content) ? officePreview._content : "OFC";
            } else {
              if (this.configService.get("onlyOfficeApiUrl") && this.configService.get("onlyOfficeApiUrl") !== "none") {
                this.officeContent = "OFC";
              }
              if (this.configService.get("collaboraBaseUrl") && this.configService.get("collaboraBaseUrl") !== "none") {
                this.officeContent = "COLLAB";
              }
            }
          } else {
            if (this.configService.get("onlyOfficeApiUrl") && this.configService.get("onlyOfficeApiUrl") !== "none") {
              this.officeContent = "OFC";
            }
            if (this.configService.get("collaboraBaseUrl") && this.configService.get("collaboraBaseUrl") !== "none") {
              this.officeContent = "COLLAB";
            }
          }
        });

        setTimeout(() => {
          this.changeDetectorRef.markForCheck();
        }, 300);
    });

    this.ownCloudService.isLoggedIn().pipe(filter(v => !!v)).pipe(take(1)
      , switchMap(() => {
        this.isLoading = true;
        this.changeDetectorRef.markForCheck();
        return this.ownCloudService.listAllFiles("/");
      })).subscribe(v => {
        this.dataSource.data = this.sortFileList(v.filter(item => !!item?.name));
        this.isLoading = false;
        this.updateTotal(v);
        this.files$.next(v);
      });
    this.configService.currentLanguage.pipe(takeUntil(this.isAlive$)).subscribe((lang: string) => {
      this.lang = lang;
      if (this.lang === "de") {
        this.locale = de;
      }
      if (this.lang === "fr") {
        this.locale = fr;
      }
      if (this.lang === "ru") {
        this.locale = ru;
      }
      if (this.lang === "en") {
        this.locale = enUS;
      }
    });

    if (!this.isLoggedIn) {
      setTimeout(() => {
        this.reloadData();
      }, 300);
    }

  }

  ngOnDestroy(): void {
    this.isAlive$.next(false);
    this.isAlive$.complete();
    this.fileUploadSubscriptions?.forEach(v => v?.unsubscribe());
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
    this.changeDetectorRef.markForCheck();
  }

  getFileSize(size) {
  //   return this.ownCloudService.getFileSize(size, this.lang);
    return filesize(size, { locale: this.lang });
  }

  sharedWithMe() {
    this.currentFolder = null;
    this.resetData();
    this.isLoading = true;
    this.currentFilter.next("shared_with_you");
    this.changeDetectorRef.markForCheck();
    this.ownCloudService.sharedWithMe().subscribe(v => {
      this.dataSource.data = this.sortFileList(v.filter(v => v.name));
      this.isLoading = false;
      this.updateTotal(v);
    });
  }

  sharedByLink() {
    this.resetData();
    this.currentFolder = null;
    this.isLoading = true;
    this.currentFilter.next("shared_by_link");
    this.changeDetectorRef.markForCheck();
    this.ownCloudService.sharedByLink().subscribe(v => {
      this.dataSource.data = this.sortFileList(v.filter(v => v.name));
      this.isLoading = false;
      this.updateTotal(v);
    });
  }

  sharedWithOthers() {
    this.resetData();
    this.currentFolder = null;
    this.isLoading = true;
    this.currentFilter.next("shared_with_others");
    this.changeDetectorRef.markForCheck();
    this.ownCloudService.sharedWithOthers().subscribe(v => {
      this.dataSource.data = this.sortFileList(v.filter(v => v.name));
      this.isLoading = false;
      this.updateTotal(v);
    });
  }

  getFavoriteFiles() {
    this.resetData();
    this.currentFolder = null;
    this.isLoading = true;
    this.currentFilter.next("favorites");
    this.changeDetectorRef.markForCheck();
    this.ownCloudService.getFavoriteFiles().subscribe(v => {
      this.dataSource.data = this.sortFileList(v.filter(v => v.name));
      this.isLoading = false;
      this.updateTotal(v);
    });
  }

  getFilesByTags(tags) {
    this.selection.clear();
    this.currentFilter.next("tags");
    this.currentFolder = null;
    this.dataSource.data = [];
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();
    this.ownCloudService.getFilesByTags(tags).subscribe(v => {
      this.dataSource.data = this.sortFileList(v.filter(v => v.name));
      this.isLoading = false;
      this.updateTotal(v);
    });
  }

  resetFilter() {
    this.currentFilter.next("all");
    if (this.searchControl.value !== "") {
      this.searchControl.patchValue("");
    }
  }

  home() {
    this.selection.clear();
    this.resetFilter();
    this.currentFolder = null;
    this.dataSource.data = this.sortFileList(this.files$.value.filter(v => v.name));
    this.updateTotal(this.files$.value);
    this.ownCloudService.listAllFiles("/").subscribe(v => {
      this.dataSource.data = this.sortFileList(v.filter(v => v.name));
      this.teamFolders = this.dataSource.data.filter(f => (!!f.mountType && f.mountType === "group"));
      this.isLoading = false;
      this.updateTotal(v);
      this.files$.next(v);
      setTimeout(() => {
        this.scrollHighlightInsideOwncloudFiles();
        this.hideFolderHighlight();
      }, 1000);
    });
  }

  hideFolderHighlight() {
    setTimeout(() => {
      this.newFolderId = null;
      this.changeDetectorRef.markForCheck();
    }, 3000);
  }

  resetData() {
    this.selection.clear();
    this.dataSource.data = [];
    this.totalFiles = 0;
    this.totalFolders = 0;
    this.fileToUpdate = null;
    this.changeDetectorRef.markForCheck();
  }

  backPreviousFolder() {
    const folders = this.currentFolder.path.split("/");
    folders.pop();
    const path = folders.join("/");
    if (path === "" || path === "/") {
      this.currentFolder = null;
      this.home();
    } else {
      this.openFolder({ type: "dir", path: path, name: path.slice(path.lastIndexOf("/") + 1, path.length) });
    }
  }

  async openFolder(file) {
    if (this.isPending) {
      return;
    }
    if (file.type === "dir") {
      this.resetData();
      this.resetFilter();
      this.currentFolder = file;
      this.isLoading = true;
      this.ownCloudService.listAllFiles(file.path).subscribe(v => {
        this.currentFolder = v[0];
        this.isLoading = false;
        this.dataSource.data = this.sortFileList(v.slice(1, v.length));
        this.updateTotal(this.dataSource.data);
        setTimeout(() => {
          this.scrollHighlightInsideOwncloudFiles();
          this.hideFolderHighlight();
        }, 1000);
        this.changeDetectorRef.markForCheck();
      }, () => {
        this.isLoading = false;
      });
    } else {
      this.selectedFile = file;
      if (!this.isNextCloud) {
        const url = await this.ownCloudService.oc.files.getPathForFileId(file.fileId);
      }
    }
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: FileInfo): string {
    if (!row) {
      return `${this.isAllSelected() ? "deselect" : "select"} all`;
    }
    return `${this.selection.isSelected(row) ? "deselect" : "select"} row ${row.name}`;
  }

  attachFile(e) {
    const files = e.target.files || e.dataTransfer.files;
    if (!files.length) return;
    // Upload files
    const uploadPromises = [];
    this.uploadedList = [];
    let fileExists = [];
    for (const file of files) {
      this.addFileToUploadProgress(file);
      fileExists.push(this.checkIfElementExists(file));
    }
    const totalFileExists = fileExists.filter(v => !!v).length;
    if (totalFileExists > 0) {
      if (totalFileExists > 1) {
        this.translate.get("MULTIPLE_FILES_ALREADY_EXISTS").pipe(take(1)).subscribe(text => this.snackbar.open(text, null, MailUtils.getSnackbarOption()));
      } else {
        this.translate.get("FILE_ALREADY_EXISTS", { fileName: fileExists.find(v => !!v)?.name }).pipe(take(1)).subscribe(text => this.snackbar.open(text, null, MailUtils.getSnackbarOption()));
      }
      return;
    }
    this.fileUploadHandler(files);
  }

  fileUploadHandler(files: File[]) {
    const filesArray = Array.isArray(files) ? files : Array.from(Object.values(files));
    const fileUploadObservables = filesArray.map(file => this.ocUpload(file, file["webkitRelativePath"] || file["name"], null, false, false));
    const uploadSubscription = from(fileUploadObservables).pipe(
      concatMap(obs => obs),
      finalize(() => {

        this.uploading.next(false);
        this.progress$.next({});
        if (!this.isCancelUpload) {
          if (!this.uploadFail) {
            this.translate.get("FILES_HAVE_UPLOADED").pipe(take(1)).subscribe(text => this.vncLibraryService.openSnackBar(text, "checkmark", "", "", 3000, "bottom", "left").subscribe());
          }
        }
        setTimeout(() => {
          this.reloadData();
          this.isCancelUpload = false;
        }, 1000);
        (<HTMLInputElement>document.getElementById("selectFile")).value = "";
      })
    ).subscribe(r => {

    }, err => {
      this.uploadFail = true;
      this.translate.get("FILE_ALREADY_EXISTS", { fileName: files[0]?.name }).pipe(take(1)).subscribe(text => this.vncLibraryService.openSnackBar(text, "block", "", "", 3000, "bottom", "left").subscribe());
      setTimeout(() => {
        this.uploadFail = false;
      }, 500);
    });
    this.fileUploadSubscriptions.push(uploadSubscription);
  }

  ocUpload(file, path, overwrite = null, emitSuccess = true, addToProgress = true): any {
    this.uploading.next(true);
    let basePath = this.currentFolder ? this.currentFolder.path : "";
    let relativePath = basePath + "/" + path;
    this.changeDetectorRef.markForCheck();
    if (addToProgress) {
      this.addFileToUploadProgress(file);
    }
    const extraHeaders = {};
    if (file.lastModifiedDate) {
      extraHeaders["X-OC-Mtime"] = "" + file.lastModifiedDate.getTime() / 1000;
    } else if (file.lastModified) {
      extraHeaders["X-OC-Mtime"] = "" + file.lastModified / 1000;
    }
    return this.ownCloudService.addFileToOwn(relativePath, file, {
      headers: extraHeaders,
      overwrite: overwrite
    }).pipe(map((response) => {
      if (response["type"] === 1) {
        this.onProgress(response , file);
      }
    }));
  }

  removeFileFromProgress(file) {
    const progress = this.progress$.value;
    delete progress[file.id];
    this.progress$.next(progress);
  }

  onProgress(e: any, file) {
    const progress = this.progress$.value;
    const percent = +e.loaded * 100 / e.total;
    progress[file.id] = {
      id: file.id,
      fileName: file.name,
      progress: parseInt(percent.toString(), 10)
    };
    this.progress$.next(progress);
  }

  private addFileToUploadProgress(file) {
    file.id = this.uploadFileUniqueId;
    this.uploadFileUniqueId++;
    this.addFileToProgress(file);
  }

  addFileToProgress(file: any) {
    const progress = this.progress$.value;
    progress[file.id] = {
      id: file.id,
      fileName: file.name,
      progress: 0
    };
    this.progress$.next(progress);
  }

  async newFolder() {
    const folderName = "/New Folder " + new Date().getTime();
    let folderPath = folderName;
    if (this.currentFolder) {
      folderPath = this.currentFolder.path + folderName;
    }
    const folder = await this.ownCloudService.oc.files.createFolder(folderPath);
    this.newFolderId = this.getFolderClass(folderName.replace("/", ""));
    this.changeDetectorRef.markForCheck();
    if (folder) {
      this.mailService.openSnackBarWithTranslation("FOLDER_HAS_BEEN_CREATED", { folderName: folderName.replace("/", "") });
      this.reloadData();
    }
  }

  getFolderClass(folderName) {
    return folderName.replace(/\s+/g, "_");
  }

  shouldHighlight(folder) {
    const isNew = this.newFolderId === this.getFolderClass(folder.name);
    if (isNew && this.checkedLastTime !== this.newFolderId) {
      this.checkedLastTime = this.newFolderId;
      this.renameFile(folder);
    }
    return isNew;
  }

  renameFile(file) {
    this.fileToUpdate = file;
    this.itemNameControl.patchValue(file.name);
    this.changeDetectorRef.markForCheck();
    setTimeout(() => {
      this.renameInput?.textInput.nativeElement.select();
    }, 100);
  }

  private scrollHighlightInsideOwncloudFiles() {
    const highlightElement = document.querySelector(".owncloud-files .highlight") as HTMLElement;
    const scrollContainer = document.querySelector(".owncloud-files") as HTMLElement;
    const topOffset = 100;

    if (!highlightElement || !scrollContainer) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = highlightElement.getBoundingClientRect();
    let nextScrollTop = scrollContainer.scrollTop;

    if (elementRect.top < containerRect.top) {
      nextScrollTop += elementRect.top - containerRect.top - topOffset;
    } else if (elementRect.bottom > containerRect.bottom) {
      nextScrollTop += elementRect.bottom - containerRect.bottom + topOffset;
    } else {
      return;
    }

    scrollContainer.scrollTo({
      top: Math.max(0, nextScrollTop),
      behavior: "smooth"
    });
  }

  async updateFile(file) {
    if (!this.itemNameControl.value || this.itemNameControl.value.trim().length === 0) {
      this.cancelUpdate();
      return;
    }
    if (this.itemNameControl.value.indexOf("/") !== -1) {
      this.mailService.openSnackBarWithTranslation("FILE_NAME_CANNOT_CONTAIN");
      return;
    }
    if (this.dataSource.data.find(v => v.name === this.itemNameControl.value && v.type === file.type && v.fileId === file.fileId)) {
      if (file.type === "dir") {
        this.mailService.openSnackBarWithTranslation("MULTIPLE_FILES_ALREADY_EXISTS", { folderName: this.itemNameControl.value });
      } else {
        this.mailService.openSnackBarWithTranslation("FILE_ALREADY_EXISTS", { fileName: this.itemNameControl.value });

      }
    } else {
      let dir = "/";
      if (this.currentFolder) {
        dir = this.currentFolder.path + "/";
      }
      this.isPending = true;
      this.ownCloudService.move(file.path, dir + this.itemNameControl.value).then(updated => {
        if (updated) {
          if (file.type === "dir") {
            this.mailService.openSnackBarWithTranslation("FOLDER_SUCCESSFULLY_RENAMED");
          } else {
            this.mailService.openSnackBarWithTranslation("FILE_SUCCESSFULLY_RENAMED");
          }
          const findIndex = this.dataSource.data.findIndex(v => v.fileId === file.fileId);
          file.name = this.itemNameControl.value;
          file.path = dir + this.itemNameControl.value;
          file.pathHash = MailUtils.md5(dir + this.itemNameControl.value);
          file.urlV2 = this.ownCloudService.oc.files.getFileUrlV2(dir + this.itemNameControl.value);
          file.url = this.ownCloudService.oc.files.getFileUrl(dir + this.itemNameControl.value);
          this.dataSource.data.splice(findIndex, 1, file);
          if (this.files$.value.find(v => v.fileId === file.id)) {
            const newFiles = this.files$.value;
            newFiles.splice(newFiles.findIndex(v => v.fileId === file.id), 1, file);
            this.files$.next(newFiles);
          }
          this.isPending = false;
          this.cancelUpdate();
        }
      }).catch(err => {
        this.isPending = false;
        this.cancelUpdate();
        this.mailService.openSnackBarWithTranslation("CANNOT_RENAME");
      });
    }

  }

  updateSharedFile(file) {
    const findIndex = this.dataSource.data.findIndex(v => v.fileId === file.fileId);
    file.shareTypes = "3";
    this.dataSource.data.splice(findIndex, 1, file);
    if (this.files$.value.find(v => v.fileId === file.id)) {
      const newFiles = this.files$.value;
      newFiles.splice(newFiles.findIndex(v => v.fileId === file.id), 1, file);
      this.files$.next(newFiles);
    }
    this.changeDetectorRef.markForCheck();
  }

  updateFileData(file) {
    const findIndex = this.dataSource.data.findIndex(v => v.fileId === file.fileId);
    this.dataSource.data.splice(findIndex, 1, file);
    if (this.files$.value.find(v => v.fileId === file.id)) {
      const newFiles = this.files$.value;
      newFiles.splice(newFiles.findIndex(v => v.fileId === file.id), 1, file);
      this.files$.next(newFiles);
    }
    this.itemNameControl.patchValue("");
    this.fileToUpdate = null;
    this.changeDetectorRef.markForCheck();
  }

  cancelUpdate() {
    this.isPending = true;
    this.itemNameControl.patchValue("");
    this.fileToUpdate = null;
    setTimeout(() => {
      this.isPending = false;
    }, 500);
    this.changeDetectorRef.markForCheck();
  }

  reloadData() {
    if (this.currentFolder) {
      this.openFolder(this.currentFolder);
    } else {
      this.home();
    }
  }

  uploadFile() {
    document.getElementById("selectFile").click();
  }

  async showDetails(file) {
    let style: any = {
      width: "480px",
      maxWidth: "95%",
      height: "610px",
    };
    if (MailUtils.isMobileSize()) {
      style = {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh"
      };
    }
    this.dialog.open(OwncloudDetailDialogComponent, Object.assign({
      backdropClass: MailUtils.isMobileSize() ? "common-dialog-backdrop-no-border" : "common-dialog-backdrop",
      panelClass: "common-dialog-panel",
      disableClose: true,
      data: {
        file: file
      },
      autoFocus: true
    }, style)).afterClosed().pipe(take(1)).subscribe(() => {

    });
  }

  async download(file) {
    console.log("ownclodDownload file: ", file);
    if (this.isNextCloud) {
      const fileUrl = (file.type === "dir") ? file.urlV2 + "/" : file.urlV2;
      const downloadUrl = (environment.isCordova || environment.isElectron) ? localStorage.getItem("serverURL") + "/api/safeProxy/" + encodeURIComponent(fileUrl) : window.origin + "/api/safeProxy/" + encodeURIComponent(fileUrl);
      if (environment.isCordova) {
        if (typeof cordova !== "undefined") {
          const url = CommonUtils.addTokenToRequest(downloadUrl);
          if (device.platform === "iOS") {
            window.open(url, "_system");
          } else if (device.platform === "Android") {
            navigator.app.loadUrl(url, {
              openExternal: true
            });
          }
        }
      } else if (environment.isElectron) {
        this.mailService.electronFileDownloader(downloadUrl).subscribe(() => {
          this.mailService.openSnackBarWithTranslation("DOWNLOAD_SUCCESS");
        });
      } else {
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } else {

      let tempUrl = file.url;
      let dir = "/";
      if (this.currentFolder) {
        dir = this.currentFolder.path;
      }
      if (file.type === "dir") {
        tempUrl = `${this.ownCloudService.owncloudURL}index.php/apps/files/ajax/download.php?dir=${dir}&files=${file.name}`;
      }
      const url = await this.ownCloudService.oc.signUrl(tempUrl);

      if (environment.isCordova || environment.isElectron) {
        if (typeof cordova !== "undefined") {
          if (device.platform === "iOS") {
            window.open(url, "_system");
          } else if (device.platform === "Android") {
            navigator.app.loadUrl(url, {
              openExternal: true
            });
          }
        } else if (this.electronService.isElectron) {
          this.electronService.openExternalUrl(url);
        }
      } else {
        window.open(url, "_blank");
      }
    }
  }

  async sendToVNCtalk(file) {
  }

  async delete(agr) {
    let confirmArgs: ConfirmArgs;
    confirmArgs = { operationType: MailOperations.DeleteVNCFile, folderName: agr?.name };
    const dialogArgs = {
        width: "376px",
        maxWidth: "85vw",
        height: "190px",
        autoFocus: false,
        panelClass: "mail_confirm_dialog",
        data: confirmArgs,
        backdropClass: ["confirmation-dialog-backdrop"]
    };
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogArgs);
    dialogRef.afterClosed().subscribe(operation => {
      if (operation.confirm) {
        if (typeof agr === "function") {
          agr();
        } else {
          this.deleteFile(agr);
        }
      }
    });
  }

  deleteFile(file) {
    this.ownCloudService.delete(file.path).then(deleted => {
      if (deleted) {
        if (file.type === "dir") {
          this.mailService.openSnackBarWithTranslation("FOLDER_SUCCESSFULLY_DELETED", { folderName: `` });
        } else {
          this.mailService.openSnackBarWithTranslation("FILE_SUCCESSFULLY_DELETED", { fileName: `` });
        }

        this.removeFromList(file);
      }
    }).catch(err => {
      if (file.type === "dir") {
        this.mailService.openSnackBarWithTranslation("CANNOT_DELETE_FOLDER", { folderName: `` });
      } else {
        this.mailService.openSnackBarWithTranslation("CANNOT_DELETE_FILE", { fileName: `` });
      }
    });
  }

  removeFromList(file: any) {
    const findIndex = this.dataSource.data.findIndex(v => v.fileId === file.fileId);
    const data = this.dataSource.data;
    data.splice(findIndex, 1);
    this.dataSource.data = this.sortFileList(data);
    this.updateTotal(this.dataSource.data);
    if (this.files$.value.find(v => v.fileId === file.id)) {
      const newFiles = this.files$.value;
      newFiles.splice(newFiles.findIndex(v => v.fileId === file.id), 1);
      this.files$.next(newFiles);
    }
  }

  async shareWithUsers(file) {
    let style: any = {
      width: "480px",
      maxWidth: "95%",
      height: "610px",
    };
    if (MailUtils.isMobileSize()) {
      style = {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh"
      };
    }
    const { OwncloudShareDialogComponent } = await import(
      "src/app/shared/components/owncloud-share/owncloud-share.component");
    this.dialog.open(OwncloudShareDialogComponent, Object.assign({
      backdropClass: MailUtils.isMobileSize() ? "common-dialog-backdrop-no-border" : "common-dialog-backdrop",
      panelClass: "common-dialog-panel",
      disableClose: true,
      data: {
        file: file
      },
      autoFocus: true
    }, style)).afterClosed().pipe(take(1)).subscribe((data) => {
      if (data && !!data.shared && !!data.users && (data.users.length > 0)) {
        data.users.forEach(sharee => {
          const payload = {
            format: "json",
            permissions: parseInt(data.permission, 10),
            shareType: sharee.shareType,
            shareWith: sharee.shareWith,
            label: sharee.fullName,
            password: (sharee.shareType === 4) ? MailUtils.randomId(10, true) : null
          };
          this.ownCloudService.shareFileWithLink(file.path, payload).pipe(take(1)).subscribe(v => {
            this.mailService.openSnackBarWithTranslation("SHARED");
            this.updateSharedFile(file);
          }, err => {
            this.mailService.openSnackBarBlockWithTranslation("CANNOT_SHARE");
            console.log("[sharePublicLink] shareFileWithLink err", err);
          });
        });
      }
    });
  }

  @HostListener("document:keydown.esc")
  onEsc(): void {
    if (this.fileToUpdate) {
      this.cancelUpdate();
      return;
    }
    this.matDialogRef?.close();
  }

  @HostListener("document:keydown.enter", ["$event"])
  onEnter(event: KeyboardEvent): void {
    if (!this.fileToUpdate || this.isPending) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target?.closest(".rename-form")) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.updateFile(this.fileToUpdate);
  }

  async sharePublicLink(file, data) {
    let style: any = {
      width: "480px",
      maxWidth: "95%",
      height: "610px",
    };
    if (MailUtils.isMobileSize()) {
      style = {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh"
      };
    }
    const { OwncloudPublicDialogComponent } = await import(
      "src/app/shared/components/owncloud-public/owncloud-public.component");
    this.dialog.open(OwncloudPublicDialogComponent, Object.assign({
      width: "480px",
      maxWidth: "95%",
      height: "610px",
      backdropClass: MailUtils.isMobileSize() ? "common-dialog-backdrop-no-border" : "common-dialog-backdrop",
      panelClass: "common-dialog-panel",
      disableClose: true,
      data: {
        file: file,
        data: data
      },
      autoFocus: true
    }, style)).afterClosed().pipe(take(1)).subscribe((data) => {
      if (data && data.shared) {
        let expireDate = "";
        if (data.expirationDate) {
          expireDate = moment(data.expirationDate).format("DD-MM-YYYY");
        }
        let shareData = {
          expireDate: expireDate,
          format: "json",
          permissions: data.permission,
          shareType: 3,
          password: data.password,
          name: data.name
        };
        if (this.isNextCloud) {
          shareData["label"] = data.name;
          this.ownCloudService.shareFileWithLink(file.path, shareData).pipe(take(1)).subscribe(res => {
            console.log("[sharePublicLink] shareFileWithLink", res);
            if (!!res && (res.length > 0)) {
              this.mailService.openSnackBarWithTranslation("COPIED_TO_CLIPBOARD");
              let link = `URL: ${res}`;
              if (data.password) {
                this.translate.get("COMMON.PASSWORD").pipe(take(1)).subscribe(text => {
                  link = `${link} ${text}: ${data.password}`;
                });
              }
              MailUtils.copyToClipboard([link]);
              this.updateSharedFile(file);
            }
          }, err => {
            if (err === MailConstants.OWNCLOUD_CANNOT_SHARE_PASSWORD) {
              this.mailService.openSnackBarBlockWithTranslation("CANNOT_SHARE_PASSWORD", null, 10000);
            } else if (err === MailConstants.OWNCLOUD_CANNOT_SHARE) {
              this.mailService.openSnackBarBlockWithTranslation("CANNOT_SHARE", null, 10000);
            } else {
              this.mailService.openSnackBarBlock(err, 10000);
            }
            shareData["expirationDate"] = data.expirationDate;
            this.sharePublicLink(file, shareData);
            console.error("[sharePublicLink] shareFileWithLink err", err);
          });
        } else {
          this.ownCloudService.oc.shares.shareFileWithLink(file.path, { expireDate: expireDate, format: "json", permissions: data.permission, shareType: 3, password: data.password, name: data.name }).then(v => {
            if (v && v.shareInfo) {
              this.mailService.openSnackBarWithTranslation("COPIED_TO_CLIPBOARD");
              let link = `URL: ${v.shareInfo.url}`;
              if (data.password) {
                this.translate.get("COMMON.PASSWORD").pipe(take(1)).subscribe(text => {
                  link = `${link} ${text}: ${data.password}`;
                });
              }
              MailUtils.copyToClipboard([link]);
              this.updateSharedFile(file);
            }

          }).catch(err => {
            this.mailService.openSnackBarWithTranslation("CANNOT_SHARE", null, 10000);
          });
        }
      }
    });
  }

  downloadAll() {
    let dir = "/";
    if (this.currentFolder) {
      dir = this.currentFolder.path;
    }
    if (this.isNextCloud) {
      const filenames = this.selection.selected.map(e => `"${e.name}"`);
      const ncUrl = this.ownCloudService.owncloudURL + "remote.php/dav/files/" + this.ownCloudService.currentUser.id + dir + "?accept=zip&files=[" + filenames.join(",") + "]";

      const downloadUrl = (environment.isCordova || environment.isElectron) ? localStorage.getItem("serverURL") + "/api/safeProxy/" + encodeURIComponent(ncUrl) : window.origin + "/api/safeProxy/" + encodeURIComponent(ncUrl);
      if (environment.isCordova) {
        if (typeof cordova !== "undefined") {
          const url = CommonUtils.addTokenToRequest(downloadUrl);
          if (device.platform === "iOS") {
            window.open(url, "_system");
          } else if (device.platform === "Android") {
            navigator.app.loadUrl(url, {
              openExternal: true
            });
          }
          setTimeout(() => {
            this.selection.clear();
          }, 200);
        }
      } else if (environment.isElectron) {
        this.mailService.electronFileDownloader(downloadUrl).subscribe(() => {
          this.mailService.openSnackBarWithTranslation("DOWNLOAD_SUCCESS");
          setTimeout(() => {
            this.selection.clear();
          }, 200);
        });
      } else {
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = "download.zip";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => {
          this.selection.clear();
        }, 200);
      }
    } else {
      const params = [];
      for (let file of this.selection.selected) {
        params.push("files[]=" + file.name);
      }
      const tempUrl = `${this.ownCloudService.owncloudURL}index.php/apps/files/ajax/download.php?dir=${dir}&${params.join("&")}`;
      this.ownCloudService.oc.signUrl(tempUrl).then(url => {
        if (environment.isCordova || environment.isElectron) {
          if (typeof cordova !== "undefined") {
            if (device.platform === "iOS") {
              window.open(url, "_system");
            } else if (device.platform === "Android") {
              navigator.app.loadUrl(url, {
                openExternal: true
              });
            }
          } else if (this.electronService.isElectron) {
            this.electronService.openExternalUrl(url);
          }
        } else {
          window.open(url, "_blank");
        }
      }).catch(err => {
        this.mailService.openSnackBarWithTranslation("CANNOT_DOWNLOAD");
      });
    }

  }

  deleteAll() {
    this.delete(() => {
      for (let file of this.selection.selected) {
        this.deleteFile(file);
      }
      this.selection.clear();
    });
  }

  resetSelection() {
    this.selection.clear();
    this.selectionText = "";
    this.changeDetectorRef.markForCheck();
  }

  onFileClickedMobile(file) {
    if (this.fileToUpdate && this.fileToUpdate.fileId === file.fileId) {
      return;
    }
    if (file.type === "dir") {
      this.openFolder(file);
    } else {
      this.previewDocument(file);
    }
  }

  async previewDocument(document) {
    // console.log("previewDocument: ", this.convRepository.currentUser, document);
    const style: any = {
      width: "100vw",
      maxWidth: "100vw",
      height: "100vh"
    };
    const triggerEvent = new Subject<any>();
    triggerEvent.pipe(takeUntil(this.isAlive$)).subscribe(v => {
      const file = this.dataSource.data.find(f => f.fileId === v.document.id);
      switch (v?.action) {
        case "share": {
            this.shareWithUsers(file);
          break;
        }
        case "download": {
            this.download(file);
          break;
        }
        case "public": {
            this.sharePublicLink(file, {});
          break;
        }
        default: {
          this.translate.get("UNDER_DEVELOPMENT")
          .pipe(take(1))
          .subscribe(text => {
            this.snackbar.open(text, null, { duration: 2000 });
          }); break;
        }
      }
    });

    const translationsKey = ["CLOSE", "MORE_TEXT", "GALLERY_VIEW", "SHARE", "SHARE_A_PUBLIC_LINK", "DOWNLOAD", "COPY_TO_CLIPBOARD", "SEND_TO_OWNCLOUD"];
    let translations: any = {};
    this.translate.get(translationsKey).pipe(take(1)).subscribe(v => {
      translations = v;
    });
    const headerActions = [
          {
            label: translations.DOWNLOAD,
            vncIconName: "download-new",
            action: "download"
          },
    ];
    const menuActions = [
      {
        label: translations.SHARE_A_PUBLIC_LINK,
        iconName: "mdi-link-variant",
        vncIconName: "footer-links",
        action: "public"
      },
    ];

    const files = this.dataSource.data.filter(v => v.type === "file").map(v => this.mapPreviewItem(v));
    let avatarName = this.convRepository.currentUser.email;
    const fullName = this.convRepository.currentUser.firstName ? this.convRepository.currentUser.firstName + " " + this.convRepository.currentUser.lastName : this.convRepository.currentUser.lastName;
    document.avatarFallback = { email: this.convRepository.currentUser.email, fullName: fullName };
    document.avatarUrl = this.configService.avatarURL + "/" + avatarName + ".jpg?v=" + new Date().getTime();
    let fileExtension = (document.name.split(".").pop() || "").toLowerCase();
    let isCommonMediaFile = MailUtils.isImage(fileExtension)
      || MailUtils.isAudio(fileExtension)
      || MailUtils.isSupportedVideo(fileExtension);

      const filesList = this.dataSource.data.filter(v => {
        let fileExtensionInside = (v.name.split(".").pop() || "").toLowerCase();
        if (((MailUtils.isImage(fileExtensionInside))
          || (MailUtils.isAudio(fileExtensionInside))
          || (MailUtils.isSupportedVideo(fileExtensionInside))) && v.type === "file") {
            return v;
        }
      }).map(v => this.mapPreviewItem(v));

    const rawfiles = this.dataSource.data.filter(v => v.type === "file" && (v.fileType === "Image" || v.fileType === "Video")).map(v => this.mapPreviewItemGallery(v));
    // console.log("previewDocument process1 isCommonMediaFile, viewermode: ", isCommonMediaFile, rawfiles);

      if (isCommonMediaFile) {
        const ddocument = this.mapPreviewItem(document);
        try {
          if (document.fileType.toLowerCase().startsWith("image")) {
            ddocument.fileType = "image";
            ddocument.filetype = "image";
            ddocument.thumbnail_url = ddocument.url;
            ddocument["documentTypeImage"] = true;
            if (!!ddocument.type) {
              delete ddocument.type;
            }
          }
        } catch (error) {

        }

        let contextMenuActions = [
          {
            label: translations.COPY_TO_CLIPBOARD,
            iconName: "mdi-content-copy",
            vncIconName: "content-copy",
            action: "copy"
          },
        ];

        this.dialog.open(DocumentPreviewComponent, Object.assign({
          backdropClass: "vnc-dialog-backdrop",
          panelClass: "vnc-preview-dialog-panel",
          disableClose: true,
          hasBackdrop: false,
          data: {
            isElectron: true,
            document: ddocument,
            selectedId: ddocument.id,
            viewerMode: "office",
            forMobile: MailUtils.isMobileSize(),
            files: rawfiles,
            menuActions: menuActions,
            contextMenuActions: contextMenuActions,
            showMoreOptionIcon: false,
            headerActions: headerActions,
            triggerEvent: triggerEvent,
            closeText: translations.CLOSE,
            // menuText: translations.MORE_TEXT,
            imageZoomSlider: true,
          },
          autoFocus: false
        }, style));
      } else {
        if (MailUtils.isDocument(fileExtension)) {
          if (fileExtension.toLowerCase() === "pdf") {
              let pdfDoc = this.mapPreviewItem(document);
              console.log("previewDocument mappedPDFdoc: ", pdfDoc);
              const url = (this.isNextCloud) ? encodeURIComponent(this.getNextCloudProxyUrl(document.urlV2)) : encodeURIComponent(await this.ownCloudService.oc.signUrl(document.urlV2));
              pdfDoc.url = (this.isNextCloud) ? url : encodeURIComponent(url);
              this.dialog.open(DocumentPreviewComponent, Object.assign({
                backdropClass: "vnc-dialog-backdrop",
                panelClass: "vnc-preview-dialog-panel",
                disableClose: true,
                hasBackdrop: false,
                data: {
                  forMobile: MailUtils.isMobileSize(),
                  document: pdfDoc,
                  selectedId: document.fileId,
                  closeText: translations.CLOSE,
                  menuText: translations.MORE_TEXT,
                  menuActions: menuActions,
                  ownCloudItem: true,
                  headerActions: headerActions,
                  files: (fileExtension === "pdf" || fileExtension === "Pdf") ? [] : filesList.reverse(),
                  triggerEvent: triggerEvent,
                  viewerMode: "pdf",
                  imageZoomSlider: false,
                },
                autoFocus: false
              }, style));
          } else {
            const useCollabora = this.officeContent === "COLLAB";

              const style: any = (MailUtils.isOnMobileDevice() || useCollabora)
              ? {
                maxHeight: "100vh",
                maxWidth: "100vw",
                height: "100vh",
                width: "100vw",
              }
              : {
                maxHeight: "90vh",
                maxWidth: "90vw",
                height: "90vh",
                width: "90vw",
              };
              this.ownCloudService.getOfficeUrl(document.fileId, useCollabora).pipe(take(1)).subscribe(u => {
                console.log("XXXX", u, fileExtension);
                if (this.isNextCloud && useCollabora) {

                  let td = this.mapPreviewItem(document);
                  td.url = u;
                  td.thumbnail_url = u;
                  td.fileType = td.fileName.split(".").pop();
                  this.dialog.open(DocumentPreviewComponent, Object.assign({
                    backdropClass: "vnc-dialog-backdrop",
                    panelClass: "vnc-preview-dialog-panel",
                    disableClose: true,
                    hasBackdrop: false,
                    data: {
                      forMobile: MailUtils.isMobileSize(),
                      disableMobileFooter: true,
                      document: td,
                      selectedId: document.fileId,
                      closeText: translations.CLOSE,
                      menuText: translations.MORE_TEXT,
                      menuActions: menuActions,
                      ownCloudItem: true,
                      headerActions: headerActions,
                      files: [],
                      triggerEvent: triggerEvent,
                      collaboraContent: u,
                      apiURL: u,
                      viewerMode: "url"
                    },
                    autoFocus: false
                  }, style));


                } else {

                  const docData = {
                    fileType: fileExtension,
                    fileName: document.name,
                    url: u
                  };
                  this.dialog.open(FilePreviewDialogComponent, Object.assign({
                    autoFocus: true,
                    panelClass: "file-preview-dialog",
                    data: {
                      attachment: docData,
                      triggerEvent: triggerEvent,
                      headerActions: headerActions,
                      isOffice: true
                    }},
                    style));
                }

              });
          }
        }
      }


  }

  isMobileSize() {
    return MailUtils.isMobileSize();
  }

  private mapPreviewItem(data) {
    // console.log("[mapPreviewItem]", data);
    const fullName = this.convRepository.currentUser.firstName ? this.convRepository.currentUser.firstName + " " + this.convRepository.currentUser.lastName : this.convRepository.currentUser.lastName;
    const avatarFallback = { email: this.convRepository.currentUser?.email, fullName: fullName};
    const avatarId = (environment.isElectron) ? this.electronService.md5(this.convRepository.currentUser.email) : md5(this.convRepository.currentUser.email);
    const avatarUrl = this.configService.avatarURL + "/" + avatarId + ".jpg?v=" + new Date().getTime();
    const document: any = {
      fileName: data.name,
      type: data.fileType,
      fileType: this.getFileType(data),
      fullName: fullName,
      url: (this.isNextCloud) ? this.getNextCloudProxyUrl(data.urlV2) : data.urlV2,
      thumbnail_url: (this.isNextCloud) ? this.getNextCloudPreviewUrl(data.fileId) : data.urlV2 + "?x=64&y=64&forceIcon=0&preview=1",
      avatarUrl,
      avatarFallback,
      id: data.fileId,
      timestamp: data.lastModified
    };
    // console.log("mapPreviewItem doc: ", document);
    return document;
  }

  private mapPreviewItemGallery(data) {
    const ddocument = this.mapPreviewItem(data);
    try {
      if (!!data && !!data.fileType && data.fileType.toLowerCase().startsWith("image")) {
        ddocument.fileType = "image";
        ddocument.filetype = "image";
        ddocument.thumbnail_url = ddocument.url;
        ddocument["documentTypeImage"] = true;
        if (!!ddocument.type) {
          delete ddocument.type;
        }
      }
      if (!!data && !!data.fileType && data.fileType.toLowerCase().startsWith("video")) {
        ddocument.fileType = "video";
        ddocument.filetype = "video";
        ddocument["documentTypeImage"] = true;
        if (!!ddocument.type) {
          delete ddocument.type;
        }
      }

    } catch (error) {

    }
    return ddocument;
  }

  getFileType(data): string {
    const fileType = (data.name.split(".").pop() || "").toLowerCase();
    return fileType === "pdf" ? fileType : data.contentType;
  }

  addToFavorite(file) {
    this.ownCloudService.toggleFavorite(file.path, true).then(() => {
      file.starred = true;
      this.updateFileData(file);
    });
  }

  removeFromFavorite(file) {
    this.ownCloudService.toggleFavorite(file.path, false).then(() => {
      file.starred = false;
      this.updateFileData(file);
      if (this.currentFilter.value === "favorites") {
        this.removeFromList(file);
      }
    });
  }

  createFolder() {
    const name = "New Folder " + new Date().getTime();
    let path = "/" + name;
    if (this.currentFolder) {
      path = this.currentFolder.path + path;
    }
    this.newFolderObject = {
      type: "dir",
      name,
      path
    };
    setTimeout(() => {
      if (document.querySelector(".is-edited input")) {
        (<HTMLInputElement>document.querySelector(".is-edited input")).focus();
      }
    }, 500);
  }

  onItemCheck(value, file) {
    this.selection.toggle(file);
  }

  renameFolder(name) {
    if (name.indexOf("/") !== -1) {
      this.mailService.openSnackBarWithTranslation("FILE_NAME_CANNOT_CONTAIN");
    } else {
      this.newFolderObject = null;
      let path = "/" + name;
      if (this.currentFolder) {
        path = this.currentFolder.path + path;
      }
      this.ownCloudService.oc.files.createFolder(path).then(v => {
        if (v) {
          this.openFolder({name, path, type: "dir"});
          this.mailService.openSnackBarWithTranslation("FOLDER_HAS_BEEN_CREATED", { folderName: path.replace("/", "") });
        }
      }).catch(err => {
        this.newFolderObject = null;
        this.mailService.openSnackBarWithTranslation("CANNOT_RENAME");
      });
    }
  }

  cancelRename() {
    this.newFolderObject = null;
  }

  getTimeAgo(date: any) {

    try {
      return distanceInWordsToNow(date, { addSuffix: true, locale: this.locale });
    } catch (error) {
      console.error("[getTimeAgo] error", error);
      return "";
    }
  }

  send() {
    const urls = [];
    this.selection.selected.filter(v => v.type === "file").forEach((file) => {
      urls.push(file.url);
    });
    this.resetSelection();
    this.matDialogRef.close({urls});
  }

  filterBy(name) {
    this.currentFilter.next(name);
    if (!!document.querySelector(".owncloud-chips vnc-smart-link-filter-chip vnc-icon-container")) {
        (<HTMLElement> document.querySelector(".owncloud-chips vnc-smart-link-filter-chip vnc-icon-container")).click();
    }
    if (name === "favorites") {
        this.getFavoriteFiles();
    } else if (name === "shared_with_you") {
        this.sharedWithMe();
    } else if (name === "shared_with_others") {
        this.sharedWithOthers();
    } else if (name === "shared_by_link") {
        this.sharedByLink();
    } else {
        this.home();
    }
  }

  filterByTags(tags) {
      if (tags.length === 0) {
          this.currentFilter.next("all");
          this.home();
      } else {
          this.currentFilter.next("tag");
          this.getFilesByTags(tags);
      }
  }


    showFilterSearchFormFormMobile() {
        this.isMobileSearch = true;
    }

    clearSearch() {
      this.searchControl.patchValue("");
      this.isMobileSearch = false;
    }


    routeToMail(): void {
        this.router.navigate(["/mail/inbox"]);
    }

    getNextCloudProxyUrl(url) {
      return (environment.isCordova || environment.isElectron) ? CommonUtils.addTokenToRequest(localStorage.getItem("serverURL") + "/api/safeProxy/" + encodeURIComponent(url)) : window.location.origin + "/api/safeProxy/" + encodeURIComponent(url);
    }

    getNextCloudPreviewUrl(fileId) {
      const url = `${this.ownCloudService.owncloudURL}core/preview?fileId=${fileId}&x=32&y=32&mimeFallback=true`;
      return (environment.isCordova || environment.isElectron) ? CommonUtils.addTokenToRequest(localStorage.getItem("serverURL") + "/api/safeProxy/" + encodeURIComponent(url)) : "/api/safeProxy/" + encodeURIComponent(url);
    }

    sortFileList(v) {
      const folders = v.filter(item => item?.type === "dir").sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      const files = v.filter(item => item?.type === "file").sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      const sortedData = [...folders, ...files];
      if (this.isNextCloud) {
        const ncData = sortedData.map(d => {
          d["isNextCloud"] = true;
          if (d.fileType === "Image") {
            d["thumbnail"] = this.getNextCloudPreviewUrl(d.fileId);
          }
          return d;
        });
        return ncData;
      } else {
        return sortedData;
      }
    }

    cancelUpload() {
      this.isCancelUpload = true;
      this.fileUploadSubscriptions.forEach(sub => sub.unsubscribe());
      this.fileUploadSubscriptions = [];
      this.uploading.next(false);
    }
}
